import { readJSON, isAppLockedStored } from '../../config/storage';
import { STORAGE_KEYS, DEFAULT_LOCK_SECONDS } from '../../config/constants';
import {
  SIGNIN,
  SIGNIN_SUCCESS,
  SIGNIN_FAILURE,
  VERIFY_2FA,
  VERIFY_2FA_FAILURE,
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAILURE,
  USER_PROFILE_SUCCESS,
  USER_PROFILE_FAILURE,
  // GET_POSTS, GET_POSTS_SUCCESS, GET_POSTS_FAILURE,
  LOGOUT,
  LOGOUT_SUCCESS,
  LOGOUT_FAILURE,
  LOADER_TRUE,
  LOADER_FALSE,
  USER_PROFILE,
  DELETE_USER_PROFILE_SUCCESS,
  LAST_SEEN_TOGGLE,
  LAST_SEEN_TOGGLE_SUCCESS,
  LAST_SEEN_TOGGLE_FAILURE,
  CREATE_CONTACT_SUCCESS,
  DELETE_CONTACT_SUCCESS,
  UPDATE_CONTACT_SUCCESS,
  LIST_CONTACTS_SUCCESS,
  LIST_CONTACTS,
  LIST_CONTACTS_FAILURE,
  PASSCODE_TOGGLE_SUCCESS,
  APP_LOCK_TRIGGER,
  APP_UNLOCK,
  UPDATE_PASSCODE_SUCCESS,
  PASSCODE_TOGGLE,
  PASSCODE_TOGGLE_FAILURE,
  UPDATE_PASSCODE,
  UPDATE_PASSCODE_FAILURE,
  VERIFY_PASSCODE_SUCCESS,
  VERIFY_PASSCODE,
  VERIFY_PASSCODE_FAILURE,
} from '../constants';

// Read through `readJSON`, which never throws and deletes a corrupt entry so the
// next boot is clean. This runs at module evaluation — before any component
// mounts and outside every error boundary — so a bare `JSON.parse` here took the
// whole app down with a blank page the user could not clear by reloading or
// logging out. The old truthiness guard did not help: the string "undefined" is
// truthy, and `JSON.parse("undefined")` throws.
const storedUser = readJSON(STORAGE_KEYS.USER, {}) ?? {};

const initialState = {
  user: storedUser,
  loader: false,
  posts: [],
  user_profile: null,
  contacts: { rows: [], count: 0 },
  //App lock state
  isAppLocked: isAppLockedStored(),
  passcodeSettings: {
    status: storedUser?.data?.passcode_settings?.is_passcode_enabled ?? false,
    // 300s, matching `lock_time Int? @default(300)` in the backend schema. This
    // was 30 while the USER_PROFILE_SUCCESS branch below used 300, so a user with
    // no server value was locked out after half a minute on a cold load and then
    // silently switched to five minutes once the profile resolved.
    lockTime: storedUser?.data?.passcode_settings?.lock_time ?? DEFAULT_LOCK_SECONDS,
  },
  unlockLoader: false,
  /**
   * Count of rejected credentials — wrong password, wrong 2FA code, wrong
   * passcode. Never read as a quantity; the UI watches it for *changes* and
   * shakes the offending input when it goes up.
   *
   * A counter rather than a boolean because two rejections in a row have to be
   * distinguishable: a flag already `true` produces no change for the second
   * one, and the input would sit still on the attempt the user most needs
   * feedback on.
   */
  authRejections: 0,
};

export default function AppReducer(state = initialState, action) {
  switch (action.type) {
    case SIGNIN:
      state = {
        ...state,
        loader: true,
      };
      break;
    case SIGNIN_SUCCESS:
      state = {
        ...state,
        user: action.payload,
        loader: false,
      };
      break;
    case SIGNIN_FAILURE:
      state = {
        ...state,
        loader: false,
        authRejections: state.authRejections + 1,
      };
      break;

    // 2FA challenge step: reuses SIGNIN_SUCCESS to persist the session once the
    // code verifies; these two just drive the button loader.
    case VERIFY_2FA:
      state = {
        ...state,
        loader: true,
      };
      break;
    case VERIFY_2FA_FAILURE:
      state = {
        ...state,
        loader: false,
        authRejections: state.authRejections + 1,
      };
      break;

    case USER_PROFILE:
      state = {
        ...state,
        // loader: true,
      };
      break;
    case USER_PROFILE_SUCCESS:
      state = {
        ...state,
        user_profile: action.payload,

        passcodeSettings: {
          status: action.payload?.data?.passcode_settings?.is_passcode_enabled ?? false,
          lockTime: action.payload?.data?.passcode_settings?.lock_time ?? DEFAULT_LOCK_SECONDS,
        },
      };
      break;
    case USER_PROFILE_FAILURE:
      state = {
        ...state,
        // loader: false,
      };
      break;

    case LOGOUT:
      state = {
        ...state,
        loader: true,
      };
      break;
    // The `localStorage.removeItem('isAppLocked')` that used to sit here now runs in
    // AppMiddleware.Logout, alongside the rest of the session teardown.
    case LOGOUT_SUCCESS:
      state = {
        user: {},
        loader: false,
        isAppLocked: false,
      };
      break;
    case DELETE_USER_PROFILE_SUCCESS:
      state = {
        user: {},
        loader: false,
      };
      break;
    case LOGOUT_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    // case ADD_POST:
    //     state = {
    //         ...state,
    //         loader: true
    //     }
    //     break;
    // case ADD_POST_SUCCESS:
    //     // state.posts.unshift(action.payload)
    //     state = {
    //         ...state,
    //         posts: [action.payload, ...state.posts],
    //         loader: false
    //     }
    //     break;
    // case ADD_POST_FAILURE:
    //     state = {
    //         ...state,
    //         loader: false
    //     }
    //     break;

    case CHANGE_PASSWORD:
      state = {
        ...state,
        loader: true,
      };
      break;
    case CHANGE_PASSWORD_SUCCESS:
      state = {
        ...state,
        // posts: action.payload,
        loader: false,
      };
      break;
    case CHANGE_PASSWORD_FAILURE:
      state = {
        ...state,
        loader: false,
      };
      break;

    case LOADER_TRUE:
      state = {
        ...state,
        loader: true,
      };
      break;

    case LOADER_FALSE:
      state = {
        ...state,
        loader: false,
      };
      break;
    case LAST_SEEN_TOGGLE:
      state = { ...state, loader: true };
      break;

    case LAST_SEEN_TOGGLE_SUCCESS:
      state = {
        ...state,
        user_profile: {
          ...state.user_profile,
          data: {
            ...state.user_profile?.data,
            last_seen_enabled: action.data?.last_seen_enabled,
          },
        },
        loader: false,
      };
      break;

    case LAST_SEEN_TOGGLE_FAILURE:
      state = { ...state, loader: false };
      break;

    //CONTACT_REDUCERS
    // The `setTimeout(() => showMessage('success', 'Contact Added'), 1000)` that used
    // to run here is now dispatched from AppMiddleware.CreateContact — where the other
    // two contact mutations already raise theirs, and without the arbitrary delay.
    case CREATE_CONTACT_SUCCESS:
      return {
        ...state,
        contacts: {
          ...state.contacts,
          rows: [...state.contacts.rows, action.payload],
        },
      };
    case DELETE_CONTACT_SUCCESS:
      return {
        ...state,
        contacts: {
          ...state.contacts,
          rows: state.contacts.rows.filter((c) => c.id !== action.payload),
          count: state.contacts.count - 1,
        },
      };
    case UPDATE_CONTACT_SUCCESS:
      return {
        ...state,
        contacts: {
          ...state.contacts,
          rows: state.contacts.rows.map((c) =>
            c.id === action.payload.id ? { ...c, ...action.payload } : c
          ),
        },
      };

    case LIST_CONTACTS:
      return { ...state, loader: true };
    case LIST_CONTACTS_SUCCESS: {
      // The contacts list is PAGED now — it used to ask for 200 in one request and
      // render that as though it were everything, so contact 201 was unreachable with
      // nothing on screen saying so. `replace` marks a first page (a fresh open, or a
      // new search term); anything else extends what is held.
      const { rows = [], count = 0, replace } = action.payload ?? {};
      const held = replace ? [] : state.contacts.rows;
      const seen = new Set(held.map((row) => row?.id));
      return {
        ...state,
        contacts: {
          count,
          rows: [...held, ...rows.filter((row) => row?.id === undefined || !seen.has(row.id))],
        },
        loader: false,
      };
    }
    case LIST_CONTACTS_FAILURE:
      return { ...state, loader: false };
    //Applock reducers
    case PASSCODE_TOGGLE:
      state = { ...state, loader: true };
      break;
    case PASSCODE_TOGGLE_SUCCESS:
      state = {
        ...state,
        passcodeSettings: {
          status: action.payload.is_passcode_enabled,
          lockTime: action.payload.lock_time ?? DEFAULT_LOCK_SECONDS,
        },
        isAppLocked: !action.payload.is_passcode_enabled ? false : state.isAppLocked,
        loader: false,
      };
      break;
    case PASSCODE_TOGGLE_FAILURE:
      state = { ...state, loader: false };
      break;

    // Persistence for both of these lives in AppMiddleware.PersistAppLock /
    // PersistAppUnlock. The reducer only moves the in-memory latch.
    case APP_LOCK_TRIGGER:
      state = { ...state, isAppLocked: true };
      break;

    case VERIFY_PASSCODE:
      state = { ...state, unlockLoader: true };
      break;

    case VERIFY_PASSCODE_SUCCESS:
      state = { ...state, unlockLoader: false };
      break;

    case VERIFY_PASSCODE_FAILURE:
      state = { ...state, unlockLoader: false, authRejections: state.authRejections + 1 };
      break;

    case APP_UNLOCK:
      state = { ...state, isAppLocked: false };
      break;
    case UPDATE_PASSCODE:
      state = { ...state, loader: true };
      break;

    case UPDATE_PASSCODE_SUCCESS:
      state = {
        ...state,
        passcodeSettings: {
          ...state.passcodeSettings,
          lockTime: action.payload.lockTime ?? DEFAULT_LOCK_SECONDS,
        },
        loader: false,
      };
      break;
    case UPDATE_PASSCODE_FAILURE:
      state = { ...state, loader: false };
      break;
    default:
      break;
  }

  return state;
}
